import '@fpsm/cxy-tokens/tokens.css';

import type React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useOfferClickoutService } from '@cx-modules/hooks/use-offer-clickout-service';

import { type OffersSlimIndexProps } from '../contract';

import Container from './componets/Container';
import OfferCard from './componets/OfferCard';
import { useCallbackWhenIntersected } from './hooks/useCallbackWhenIntersected';
import { useMapRequiredNamespaces } from './hooks/useMapRequiredNamespaces';
import { useQuery } from './hooks/useQuery';
import { clickOut, impression } from './utils/analytics';
import contractToProps, { type OfferProps } from './utils/contractToProps';
import { Theme } from './styles';

const getDataLayer = () => {
  if (typeof window !== 'undefined') {
    const dataLayer = (window as Window & { dataLayer?: unknown[] }).dataLayer;
    return Array.isArray(dataLayer) ? dataLayer : [];
  }
  return [];
};

const OffersSlim: React.FC<Omit<OffersSlimIndexProps, 'dehydratedState'>> = props => {
  const {
    analyticsDataLayer = getDataLayer(),
    analyticsImpressionEventData,
    apiKey,
    clickoutService = 'OFFERS_CLICKOUT_SERVICE',
    clickoutServiceUrl,
    deviceType = 'desktop',
    disableAnalytics,
    fullPageUrl,
    gtmId = null,
    pageUrl,
    placeholders,
    recommendedMarketId,
    uncappedOfferDisplay,
    variant: variantProp,
  } = props;
  const { data } = useQuery(props);
  const hasOffers = Array.isArray(data) && data.length > 0;
  const variant = !variantProp ? 'default' : variantProp;
  const shouldUseClickoutService = clickoutService === 'OFFERS_CLICKOUT_SERVICE';

  const offers = useOfferClickoutService({
    apiKey,
    clickoutOrigin: 'offers-slim',
    clickoutServiceUrl,
    deviceType,
    fullPageUrl,
    gtmId,
    offers: data,
    pageUrl,
    recommendedMarketId,
    shouldUseClickoutService,
  });
  const namespaces = useMapRequiredNamespaces(placeholders);
  const { t } = useTranslation(namespaces ?? ['label', 'football']);

  const ref = useRef(null);
  useCallbackWhenIntersected(ref, () => {
    if (!disableAnalytics) impression(analyticsDataLayer, analyticsImpressionEventData || {}, fullPageUrl);
  });

  const handleCta = (index: number, offer: OfferProps) => {
    if (!disableAnalytics) clickOut(analyticsDataLayer, offer, index, fullPageUrl);

    window.open(offer.url, '_blank', 'noopener noreferrer');
  };

  return (
    <Theme data-testid="offers-slim" ref={ref}>
      {hasOffers && (
        <Container uncappedOfferDisplay={uncappedOfferDisplay} variant={variant}>
          {contractToProps(offers, {
            placeholders,
            translation: t,
            variant,
          }).map((offer, index) => {
            return (
              <OfferCard {...offer} key={index} onClick={() => handleCta(index, offer)} variant={variant}></OfferCard>
            );
          })}
        </Container>
      )}
    </Theme>
  );
};

export { OffersSlim };
